import { FC, useRef, useState } from 'react';
import styles from './AccountButton.module.scss';
import accountCircleLogo from 'src/redesign/assets/accountCircle.svg';
import chevronDownIcon from 'src/redesign/assets/chevronDown.svg';
import { OverlayPanel, OverlayPanelPassThroughOptions } from 'primereact/overlaypanel';
import { TieredMenu, TieredMenuPassThroughOptions } from 'primereact/tieredmenu';
import { MenuItem } from 'primereact/menuitem';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { Avatar } from 'primereact/avatar';
import c from 'clsx';
import { AccountSettingsModal } from 'src/redesign/features/Modals/AccountSettingsModal';
import { NotificationSettingsModal } from 'src/redesign/features/Modals/NotificationSettingsModal';
import { accountSelector } from 'src/redesign/features/Account/store/selectors';
import { logoutAccount } from 'src/redesign/features/Account/store/actions';
import { PaymentSettingsModal } from 'src/components/payment-settings';
import { DevelopersModal } from 'src/redesign/features/Developers/components/DevelopersModal';

const AccountInfo: FC = () => {
  const account = useAppSelector(accountSelector);

  return (
    <div className={styles.accountInfoContainer}>
      <Avatar
        className={styles.avatar}
        label={account?.first_name?.charAt(0).toUpperCase()}
        size="xlarge"
        shape="circle"
      />
      <div className={styles.accountInfoText}>
        <span className={styles.accountName}>
          {account.first_name} {account.last_name}
        </span>
        <span className={styles.accountEmail}>{account.email}</span>
      </div>
    </div>
  );
};

export const AccountButton: FC = () => {
  const accountOverlayRef = useRef<OverlayPanel>();
  const dispatch = useAppDispatch();
  const account = useAppSelector(accountSelector);
  const [visible, setVisible] = useState(false);
  const [openPaymentConfig, setPaymentConfig] = useState(false);
  const [openNotifications, setOpenNotifications] = useState(false);
  const [openDevelopers, setOpenDevelopers] = useState(false);
  const showDevelopers = process.env.FEATURE_FLAG_DEVELOPERS === 'true';

  const items: MenuItem[] = [
    {
      template: <AccountInfo />,
      className: styles.avatarCustomLabelWrapper,
    },
    {
      separator: true,
    },
    ...(account?.user_profile_url
      ? [
          {
            label: 'View Profile',
            className: styles.menuItemLink,
            icon: 'pi pi-user',
            command: () => {
              window.open(account.user_profile_url, '_blank');
            },
          },
        ]
      : []),
    {
      label: 'Account Setting',
      className: styles.menuItemLink,
      icon: 'pi pi-cog',
      command: () => {
        setVisible(true);
      },
    },
    {
      label: 'Get Paid',
      className: styles.menuItemLink,
      icon: 'pi pi-money-bill',
      command: () => {
        setPaymentConfig(true);
      },
    },
    {
      label: 'Notifications',
      className: styles.menuItemLink,
      icon: 'pi pi-bell',
      command: () => {
        setOpenNotifications(true);
      },
    },
    ...(showDevelopers
      ? [
          {
            label: 'Developers',
            className: styles.menuItemLink,
            icon: 'pi pi-code',
            command: () => {
              setOpenDevelopers(true);
            },
          },
        ]
      : []),
    {
      label: 'Logout',
      className: c(styles.menuItemLink, styles.menuItemLogout),
      icon: 'pi pi-sign-out',
      command: () => dispatch(logoutAccount()),
    },
  ];

  const overlayPtOptions: OverlayPanelPassThroughOptions = {
    root: {
      className: styles.ptOverlay,
    },
    content: {
      style: {
        padding: '0',
      },
    },
  };

  const menuItemPtOption: TieredMenuPassThroughOptions = {
    separator: {
      style: {
        margin: '8px 0',
        borderColor: '#D9D9D9',
      },
    },
    menuitem: {
      className: styles.menuItem,
    },
    label: {
      style: {
        width: '100%',
      },
    },
    root: {
      style: {
        width: '100%',
        border: 'none',
        borderRadius: '16px',
      },
    },
  };

  return (
    <>
      <button onClick={(e) => accountOverlayRef.current.toggle(e)} className={styles.button}>
        <img className={styles.logo} src={accountCircleLogo} alt="account" />
        <span className={styles.accountText}>Account</span>
        <img className={styles.chevronIcon} src={chevronDownIcon} alt="down" />
      </button>
      <OverlayPanel pt={overlayPtOptions} className={styles.overlay} ref={accountOverlayRef}>
        <TieredMenu pt={menuItemPtOption} className={styles.styledTieredMenu} model={items} />
      </OverlayPanel>
      <AccountSettingsModal isVisible={visible} setVisible={(value) => setVisible(value)} />
      <PaymentSettingsModal
        openPaymentConfig={openPaymentConfig}
        setOpenPaymentConfig={setPaymentConfig}
      />
      <NotificationSettingsModal isVisible={openNotifications} setVisible={setOpenNotifications} />
      {showDevelopers && (
        <DevelopersModal isVisible={openDevelopers} setVisible={setOpenDevelopers} />
      )}
    </>
  );
};
